import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { maxBy } from 'lodash'
import {
  PreReservation,
  PreReservationForm,
  PreReservationListItemProps,
} from 'types/med'
import {
  deletePreReservation,
  getDepartmentList,
  getPreReservation,
  getPreReservationList,
  postPreReservation,
  putPreReservation,
} from './fetch'
import { useGlobal } from 'reactn'
import { sideBarRoute } from './utils'
import MedPreReservations from 'ui/pages/med/PreReservations'
import { routePaths } from '../routes'
import { ApiErrorWithMessage } from '../client'

const PreReservations: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { pre_reservation_id } = useParams<'pre_reservation_id'>()
  const [user] = useGlobal('user')
  const [update] = useGlobal('updateInformation')
  const [departmentListItems, setDepartmentListItems] = useGlobal(
    'departmentListItems'
  )
  const [creating, setCreating] = useState<boolean>(false)
  const [preReservation, setPreReservation] = useState<PreReservation>()
  const [preReservationList, setPreReservationList] = useGlobal(
    'preReservationListItems'
  )
  const [preReservationError, setPreReservationError] = useState<
    'expired' | 'notFound' | 'reserved'
  >()

  const [selectedId, setSelectedId] = useState<
    PreReservationListItemProps['id'] | null
  >(null)

  const getPreReservationHandler = (selectedId: number) => {
    getPreReservation(selectedId)
      .then(setPreReservation)
      .catch((e) => {
        if (e instanceof ApiErrorWithMessage) {
          if (
            e.has({
              key: [
                'provisional_reservation_id',
                'provisional_reservation_access_key',
              ],
              value: 'exists',
            })
          ) {
            setPreReservationError('notFound')
            return
          }
          if (
            e.has({
              key: 'provisional_reservation_access_key',
              value: 'reserved',
            })
          ) {
            setPreReservationError('reserved')
            return
          }
          if (
            e.has({
              key: 'access_key_expiration',
              value: 'valid',
            })
          ) {
            setPreReservationError('expired')
          }
        }
      })
  }

  const postPreReservationHandler: (
    data: PreReservationForm
  ) => Promise<void> = (data: PreReservationForm) => {
    return postPreReservation(data).then(() => {
      getPreReservationList().then((data) =>
        setPreReservationList(data).then(() => {
          const last = maxBy(data, 'id')
          if (last) {
            navigate(
              `${routePaths.medical}${routePaths.reservations}/pre/${last.id}`
            )
          } else {
            navigate(`${routePaths.medical}${routePaths.reservations}/pre`)
          }
        })
      )
    })
  }

  const resendSmsHandler: () => Promise<void> = () => {
    return putPreReservation(Number(selectedId))
  }

  const cancelPreReservationHandler: () => Promise<void> = () => {
    return deletePreReservation(Number(selectedId))
      .then(() => {
        setPreReservation(undefined)
        getPreReservationList().then((data) => {
          setPreReservationList(data).then(() =>
            navigate(`${routePaths.medical}${routePaths.reservations}/pre`)
          )
        })
      })
      .catch((e) => {
        if (e instanceof ApiErrorWithMessage) {
          if (
            e.has({
              key: 'provisional_reservation_id',
              value: 'exists',
            })
          ) {
            setPreReservationError('notFound')
          }
          if (
            e.has({
              key: 'provisional_reservation_id',
              value: 'reserved',
            })
          ) {
            setPreReservationError('reserved')
          }
        }
      })
  }

  useEffect(() => {
    switch (preReservationError) {
      case 'expired':
        navigate(`${routePaths.medical}${routePaths.reservations}/pre`)
        break
      case 'notFound':
        navigate(`${routePaths.medical}${routePaths.reservations}/pre`)
        break
      case 'reserved':
        navigate(`${routePaths.medical}${routePaths.reservations}/pre`)
        break
      default:
        break
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preReservationError])

  useEffect(() => {
    const isNew =
      location.pathname ===
      `${routePaths.medical}${routePaths.reservations}/pre/new`
    setCreating(isNew)
    if (isNew) {
      setSelectedId(null)
      setPreReservation(undefined)
      return
    }
    if (isNaN(Number(pre_reservation_id)) && preReservationList?.length) {
      const id = preReservationList[0].id
      navigate(`${routePaths.medical}${routePaths.reservations}/pre/${id}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pre_reservation_id, preReservationList, location.pathname])

  useEffect(() => {
    getPreReservationList().then(setPreReservationList)
    getDepartmentList().then(setDepartmentListItems)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isNaN(Number(pre_reservation_id))) {
      const id = Number(pre_reservation_id)
      getPreReservationHandler(id)
      setSelectedId(id)
    }
  }, [pre_reservation_id])

  if (!user) return null

  return (
    <MedPreReservations
      med={user}
      update={update}
      departments={departmentListItems || []}
      selectRoute={{
        route: 'reservations/pre',
        onSelect: (to) =>
          sideBarRoute(navigate)(
            Number(pre_reservation_id) ? undefined : 'reservations/pre'
          )(to),
      }}
      preReservationSubmenu={{
        preReservations: preReservationList,
        onClickItem: (preReservationListItem) => {
          navigate(
            `${routePaths.medical}${routePaths.reservations}/pre/${preReservationListItem.id}`
          )
        },
        selectedId: preReservationList ? selectedId : null,
        creating: creating,
      }}
      creating={creating}
      currentPreReservation={preReservation}
      onSendSms={postPreReservationHandler}
      onResendSms={resendSmsHandler}
      onCancel={cancelPreReservationHandler}
      onDiscardCreate={() =>
        navigate(`${routePaths.medical}${routePaths.reservations}/pre`)
      }
      onClickCreate={() =>
        navigate(`${routePaths.medical}${routePaths.reservations}/pre/new`)
      }
    />
  )
}

export default PreReservations
